<template>
  <div>
    <v-card width="400px">
      <v-card-title>
        <DialogTitle :dialogName="$options.name">
          {{ isEditMode ? 'Edit' : 'New' }} Commission
        </DialogTitle>
      </v-card-title>
      <v-card-text v-if="formData && selectLists">
        <DateTimePicker
          v-model="formData.WhenCreated"
          label="When Created"
          :error-messages="errorMessages(vuelidate.formData.WhenCreated)"
          @blur="vuelidate.formData.WhenCreated.$touch()"
          dense
        />
        <v-select
          v-model="formData.UserId"
          label="Commission For"
          :items="selectLists.Users"
          item-text="Text"
          item-value="Value"
          :error-messages="errorMessages(vuelidate.formData.UserId)"
          @blur="vuelidate.formData.UserId.$touch()"
          dense
        />
        <CustomerPicker
          v-model="formData.CustomerId"
          v-bind:text.sync="formData.CustomerName"
          label="Customer"
          dense
          :error-messages="errorMessages(vuelidate.formData.CustomerId)"
          @blur="vuelidate.formData.CustomerId.$touch()"
        />
        <v-select
          v-model="formData.CommissionTypeId"
          label="Commission Type"
          :items="selectLists.CommissionTypes"
          item-text="Text"
          item-value="Value"
          :error-messages="errorMessages(vuelidate.formData.CommissionTypeId)"
          @blur="vuelidate.formData.CommissionTypeId.$touch()"
          dense
        />
        <v-currency-field
          v-model="formData.Amount"
          label="Amount"
          dense
          :error-messages="errorMessages(vuelidate.formData.Amount)"
          @blur="vuelidate.formData.Amount.$touch()"
        />
        <DatePicker
          v-model="formData.WhenDue"
          label="When Due"
          dense
          clearable
        />
        <v-textarea v-model="formData.Reason" label="Reason" rows="3" dense />
      </v-card-text>
      <v-card-actions
        ><v-btn color="success" class="ml-3" @click="saveClick">{{
          isEditMode ? 'Update' : 'Post'
        }}</v-btn>
        <v-btn color="error" class="ml-3" @click="cancelClick">Cancel</v-btn>
      </v-card-actions>
    </v-card>

    <DialogUtil ref="dialogUtil" /></div
></template>

<script>
import { ref, watch } from 'vue'
import { host } from '@/services/HostAPI'
import { selectListCache } from '@/services/SelectListCache'
import CustomerPicker from '@/components/Customer/CustomerPicker'
import { useVuelidate } from '@vuelidate/core'
import { required, minValue } from '@vuelidate/validators'

export default {
  name: 'Commission',
  props: {
    isDialogOpen: {
      type: Boolean,
      default: false
    },
    commissionId: {
      type: String,
      required: false
    }
  },
  components: { CustomerPicker },
  setup(props, context) {
    const dialogUtil = ref()

    watch(
      () => props.isDialogOpen,
      newVal => {
        newVal && initDialog()
      }
    )

    const formData = ref()
    const isEditMode = ref(false)
    const selectLists = ref([])

    const initDialog = () => {
      loadSelectLists()
      isEditMode.value = props.commissionId ? true : false // Typescript would help here
      if (isEditMode.value) {
        retrieveCommission(props.commissionId)
      } else {
        retrieveNewTemplate()
      }
      vuelidate.value.$reset()
    }

    const rules = {
      formData: {
        WhenCreated: { required },
        UserId: { required },
        CustomerId: { required },
        CommissionTypeId: { required },
        Amount: { required, minValue: minValue(0.01) }
      }
    }

    const vuelidate = useVuelidate(rules, { formData })

    function errorMessages(item) {
      return vuelidate.value.$invalid ? item.$errors.map(e => e.$message) : []
    }

    const retrieveCommission = async id => {
      const rq = { InfoId: id }
      const rs = await host.commission.retrieve(rq)
      formData.value = rs.Info
    }

    const retrieveNewTemplate = async () => {
      const rq = {}
      const rs = await host.commission.newTemplate(rq)
      formData.value = rs.Info
    }

    const loadSelectLists = async () => {
      const selectListNames = [
        selectListCache.name.CommissionTypes,
        selectListCache.name.Users
      ]

      selectLists.value = await selectListCache.get(selectListNames)
    }

    const cancelClick = () => {
      context.emit('close')
    }

    const saveClick = async () => {
      vuelidate.value.$touch()
      if (vuelidate.value.$invalid) {
        return
      }
      const rq = { Info: formData.value }
      const rs = isEditMode.value
        ? await host.commission.update(rq)
        : await host.commission.create(rq)
      if (rs.IsSuccess) {
        context.emit('update')
      } else {
        dialogUtil.value.inform({
          text: rs.Message,
          snackbarTimeout: 10000,
          color: 'error'
        })
      }
    }

    initDialog()
    return {
      dialogUtil,
      formData,
      isEditMode,
      selectLists,
      errorMessages,
      saveClick,
      cancelClick,
      vuelidate
    }
  }
}
</script>

<style lang="scss" scoped></style>
